/**
 * Zone labels (pure): sargam + letter names per petal, octave numbering, and positions.
 * Rendered as DOM text by MandalaLabels.tsx; the renderer also reads `labelPositions` for its backing discs.
 *
 * Octaves: Sa of ring 1 is octave 3 (C#3 when the tonic is C#), ring 2 → 4, ring 3 → 5, bindu → 2.
 * Scientific octave = floor(midi / 12) - 1 with midi = SA_OCTAVE0_MIDI_C + tonicPc + semitone + 12·(ring − 1).
 */
import { FIFTHS_ORDER, PITCH_CLASS_NAMES, SA_OCTAVE0_MIDI_C, sargamName, type Scale } from '@tonist/shared';
import { gateOf, petalCount, zoneCentre, zoneKey, type MandalaLayout, type RingIndex } from './geometry';

export interface Label {
  key: string;
  /** css px */
  x: number;
  y: number;
  ring: RingIndex;
  index: number;
  /** sargam (rings 1–2, bindu) or letter+octave (ring 3, fire ring) */
  primary: string;
  /** letter+octave under the sargam; '' where primary already is the letter */
  secondary: string;
  inScale: boolean;
  isTonic: boolean;
  /** cardinal gate 0 = E, 1 = N, 2 = W, 3 = S for palace (ring 2) petals, else -1 */
  gate: number;
}

export interface LabelPos { key: string; ring: RingIndex; index: number; x: number; y: number }

/** Inset from the canvas edge that fire-ring labels keep, in css px. */
export const EDGE_INSET = 18;

/** Letter name with scientific octave for a midi note. */
export function noteName(midi: number): string {
  const pc = ((midi % 12) + 12) % 12;
  return `${PITCH_CLASS_NAMES[pc]}${Math.floor(midi / 12) - 1}`;
}

/** MIDI of a degree given as semitones above Sa, on ring 1..3 (octave 0..2) or the bindu (ring 0 → octave -1). */
export function midiFor(tonicPc: number, semitone: number, ring: RingIndex): number {
  const octave = ring === 0 ? -1 : ring - 1;
  return SA_OCTAVE0_MIDI_C + tonicPc + semitone + 12 * octave;
}

/** Distance from (cx, cy) to the rectangle edge along direction (cos a, -sin a) (angle CCW from east, y down). */
export function distanceToEdge(width: number, height: number, cx: number, cy: number, angle: number): number {
  const dx = Math.cos(angle), dy = -Math.sin(angle);
  let t = Infinity;
  if (dx > 1e-9) t = Math.min(t, (width - cx) / dx);
  if (dx < -1e-9) t = Math.min(t, -cx / dx);
  if (dy > 1e-9) t = Math.min(t, (height - cy) / dy);
  if (dy < -1e-9) t = Math.min(t, -cy / dy);
  return t;
}

/** Fire-ring label position: just outside R3, but always at least EDGE_INSET inside the canvas. */
export function fireLabelPosition(layout: MandalaLayout, index: number): { x: number; y: number } {
  const angle = (index * Math.PI * 2) / 12;
  const want = layout.R + Math.max(16, Math.min(layout.width, layout.height) * 0.05);
  const edge = distanceToEdge(layout.width, layout.height, layout.cx, layout.cy, angle) - EDGE_INSET;
  const r = Math.max(layout.R * 1.02, Math.min(want, edge));
  return { x: layout.cx + Math.cos(angle) * r, y: layout.cy - Math.sin(angle) * r };
}

/** Positions of every label (bindu, 3 × petals, 12 fire sectors) for a petal count; ≤ 1 + 3·8 + 12 = 37 ≤ 48. */
export function labelPositions(layout: MandalaLayout, degrees: number): LabelPos[] {
  const out: LabelPos[] = [{ key: zoneKey(0, 0), ring: 0, index: 0, x: layout.cx, y: layout.cy }];
  const petals = petalCount(degrees);
  for (const ring of [1, 2, 3] as const) {
    for (let i = 0; i < petals; i++) {
      const c = zoneCentre(layout, degrees, ring, i);
      out.push({ key: zoneKey(ring, i), ring, index: i, x: c.x, y: c.y });
    }
  }
  for (let i = 0; i < 12; i++) {
    const c = fireLabelPosition(layout, i);
    out.push({ key: zoneKey(4, i), ring: 4, index: i, x: c.x, y: c.y });
  }
  return out;
}

export function computeLabels(layout: MandalaLayout, scale: Scale, tonicPc: number): Label[] {
  const degrees = scale.semitones.length;
  const positions = labelPositions(layout, degrees);
  const inScalePc = new Set(scale.semitones.map((s) => (s + tonicPc) % 12));
  return positions.map((p) => {
    if (p.ring === 4) {
      const pc = FIFTHS_ORDER[p.index]!;
      return { key: p.key, x: p.x, y: p.y, ring: p.ring, index: p.index,
        primary: PITCH_CLASS_NAMES[pc]!, secondary: '', inScale: inScalePc.has(pc), isTonic: pc === tonicPc, gate: -1 };
    }
    if (p.ring === 0) {
      return { key: p.key, x: p.x, y: p.y, ring: p.ring, index: 0,
        primary: 'Sa', secondary: noteName(midiFor(tonicPc, 0, 0)), inScale: true, isTonic: true, gate: -1 };
    }
    const semitone = p.index < degrees ? scale.semitones[p.index]! : 12;
    const letter = noteName(midiFor(tonicPc, semitone, p.ring));
    const sargam = sargamName(semitone);
    const isSa = p.index === 0 || p.index === degrees;
    return { key: p.key, x: p.x, y: p.y, ring: p.ring, index: p.index,
      primary: p.ring === 3 ? letter : sargam, secondary: p.ring === 3 ? '' : letter,
      inScale: true, isTonic: isSa, gate: p.ring === 2 ? gateOf(degrees, p.index) : -1 };
  });
}
